<template>
  <div v-if="headings.length > 0" class="p-content-sidebar">
    <nav class="p-content-sidebar__nav">
      <ul class="p-content-sidebar__list">
        <!-- Content Title -->
        <li v-if="title" class="p-content-sidebar__item">
          <a
            href="#"
            class="p-content-sidebar__link f-body-14 t-fg-primary"
            :class="{ 'p-content-sidebar__link--active': activeHeading === TITLE_ID }"
            @click.prevent="handleTitleClick"
          >
            {{ toTitleCase(title) }}
          </a>
        </li>

        <!-- Headings Navigation -->
        <li v-for="(heading, index) in headings" :key="index" class="p-content-sidebar__item">
          <a
            :href="`#${heading.id}`"
            class="p-content-sidebar__link f-body-14-medium t-fg-tertiary"
            :class="{ 'p-content-sidebar__link--active': activeHeading === heading.id }"
            @click.prevent="handleHeadingClick(heading.id)"
          >
            {{ toTitleCase(heading.text) }}
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from 'vue'

interface Heading {
  id: string
  text: string
  element: HTMLElement
}

const props = defineProps<{
  content: string
  title?: string
}>()

const TITLE_ID = '__article-title__'

const headings = ref<Heading[]>([])
const activeHeading = ref<string>('')

const toTitleCase = (str: string) =>
  str.toLocaleLowerCase('tr-TR').replace(/(^|\s)\S/g, (s) => s.toLocaleUpperCase('tr-TR'))

// Single source of truth: read headings directly from the rendered DOM and
// tag IDs on the same elements we'll later look up. This avoids the previous
// race where the parsed-string headings and the v-html'd DOM could disagree
// on SPA navigation (the DOM hadn't flushed yet when we tried to tag IDs).
const findActiveArticleBody = (): HTMLElement | null => {
  if (typeof window === 'undefined') return null
  const all = document.querySelectorAll<HTMLElement>('.content-details__body')
  for (const el of all) {
    if (el.offsetParent !== null) return el
  }
  return all[0] || null
}

const syncHeadingsFromDom = () => {
  const body = findActiveArticleBody()
  if (!body) {
    headings.value = []
    return
  }

  const h2Elements = body.querySelectorAll('h2')
  const cleaned: Heading[] = []
  h2Elements.forEach((h2, index) => {
    const raw = h2.textContent || ''
    const text = raw.replace(/[\u00A0\u200B-\u200D\uFEFF]/g, '').trim()
    if (!text) return

    if (!h2.id) h2.id = `heading-${index}`
    cleaned.push({ id: h2.id, text, element: h2 as HTMLElement })
  })

  headings.value = cleaned
}

let bodyObserver: MutationObserver | null = null

const observeArticleBody = () => {
  if (typeof window === 'undefined') return
  const body = findActiveArticleBody()
  if (!body) return
  if (bodyObserver) bodyObserver.disconnect()
  bodyObserver = new MutationObserver(() => {
    syncHeadingsFromDom()
    updateActiveHeading()
  })
  bodyObserver.observe(body, { childList: true, subtree: true })
}

const disconnectBodyObserver = () => {
  if (bodyObserver) {
    bodyObserver.disconnect()
    bodyObserver = null
  }
}

const waitForElementById = (
  id: string,
  maxAttempts = 20,
  intervalMs = 50,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    let attemptsRemaining = maxAttempts
    const tryFind = () => {
      const el = document.getElementById(id)
      if (el) return resolve(el)
      if (attemptsRemaining <= 0) return resolve(null)
      attemptsRemaining -= 1
      setTimeout(tryFind, intervalMs)
    }
    tryFind()
  })
}

const handleTitleClick = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
  activeHeading.value = TITLE_ID
}

const handleHeadingClick = async (headingId: string) => {
  // Ensure IDs are present in rendered DOM
  syncHeadingsFromDom()
  await nextTick()

  const element = document.getElementById(headingId) || (await waitForElementById(headingId))
  if (!element) return
  const headerEl = document.querySelector('.header') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 0

  const targetY = window.scrollY + element.getBoundingClientRect().top - (headerHeight + 8)

  window.scrollTo({
    top: targetY,
    behavior: 'smooth',
  })

  activeHeading.value = headingId
}

let rafId: number | null = null

const updateActiveHeading = () => {
  if (headings.value.length === 0 && !props.title) return

  const headerEl = document.querySelector('.header') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 80
  const triggerY = headerHeight + 16

  let activeId = props.title ? TITLE_ID : headings.value[0]?.id || ''
  for (const heading of headings.value) {
    const el = document.getElementById(heading.id)
    if (!el) continue
    if (el.getBoundingClientRect().top <= triggerY) {
      activeId = heading.id
    } else {
      break
    }
  }

  activeHeading.value = activeId
}

const onScroll = () => {
  if (rafId !== null) return
  rafId = requestAnimationFrame(() => {
    updateActiveHeading()
    rafId = null
  })
}

const startScrollSpy = async () => {
  if (typeof window === 'undefined') return
  await nextTick()
  updateActiveHeading()
  window.addEventListener('scroll', onScroll, { passive: true })
}

const stopScrollSpy = () => {
  if (typeof window === 'undefined') return
  window.removeEventListener('scroll', onScroll)
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

onMounted(async () => {
  // Wait one tick so we don't depend on parent render order — the article
  // body must be in the DOM before we sync/observe it.
  await nextTick()
  syncHeadingsFromDom()
  observeArticleBody()
  startScrollSpy()
})

onBeforeUnmount(() => {
  disconnectBodyObserver()
  stopScrollSpy()
})

watch(
  () => props.content,
  async () => {
    // Content changed (e.g., tab switch) — the active body element may also
    // have changed, so re-attach the observer and re-sync.
    observeArticleBody()
    syncHeadingsFromDom()
    await nextTick()
    updateActiveHeading()
  },
)
</script>
